import {
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSearchbar,
    IonSegment,
    IonSegmentButton,
    IonTitle,
    IonToolbar,
    RefresherEventDetail,
    useIonToast
} from "@ionic/react";

import {
    arrowBackOutline,
    checkmarkCircleOutline,
    americanFootballOutline,
    hourglassOutline,
    peopleOutline,
    personOutline,
    refreshOutline,
    timerOutline,
    trophyOutline
} from "ionicons/icons";
import Cookies from "js-cookie";
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { useHistory, useParams } from "react-router-dom";
import "./FantasyDraft.css";

type DraftStatus = "scheduled" | "active" | "completed";
type FantasyDraftPollState = {
  success: boolean;
  serverNow: string;
  leagueName: string;
  status: "scheduled" | "active" | "completed";
  version: number;
  pickNumber: number;
  totalPicks: number;
  secondsPerPick: number;
  draftStartAt: string;
  turnStartedAt: string;
  deadlineAt: string;
  myTeamId: string;
  draftOrderRandomization?: DraftOrderRandomization;
  currentTeam: {
    id: string;
    name: string;
    manager: string;
  } | null;
};

interface DraftTeam {
    id: string;
    name: string;
    manager: string;
}

interface AvailablePlayer {
    id: string;
    name: string;
    positionId: string;
    position: string;
    teamId: string;
    teamName: string;
    seasonProjection: number;
}


interface DraftPick {
    pickNumber: number;
    teamId: string;
    draftTeamName: string;
    playerId: string;
    playerName: string;
    position: string;
    playerTeamName: string;
    selectedAt: string;
}


interface DraftRosterPlayer {
    id: string;
    name: string;
    position: string;
    slot: string;
}

interface DraftOrderRandomization {
    isCommissioner: boolean;
    canRandomize: boolean;
    wasRandomized: boolean;
    requiredTeams: number;
    registeredTeams: number;
    reason: string;
}

interface DraftState {
    success: boolean;
    serverNow: string;
    leagueName: string;
    status: DraftStatus;
    draftOrderRandomization?: DraftOrderRandomization;
    draftFormat?: string;
    firstRoundOrder?: DraftTeam[];
    version: number;
    pickNumber: number;
    totalPicks: number;
    secondsPerPick: number;
    draftStartAt: string;
    turnStartedAt: string;
    deadlineAt: string;
    currentTeam: DraftTeam | null;
    myTeamId: string;
    availablePlayers: AvailablePlayer[];
    draft: DraftPick[];
    myRoster: DraftRosterPlayer[];
}


type PositionFilter =
    | "ALL"
    | "QB"
    | "RB"
    | "WR"
    | "K"
    | "DEF";

function getInitial(name: string): string {
    return name.trim().charAt(0).toUpperCase() || "?";
}

function formatRemainingTime(
    deadlineAt: string,
    now: number
): string {
    const deadline = new Date(deadlineAt).getTime();

    if (Number.isNaN(deadline)) {
        return "00:00";
    }

    const remainingSeconds = Math.max(
        0,
        Math.ceil((deadline - now) / 1000)
    );

    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(
        seconds
    ).padStart(2, "0")}`;
}

function formatDraftDate(value: string): string {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "Hora por confirmar";
    }

    return new Intl.DateTimeFormat("es-MX", {
        hour: "2-digit",
        minute: "2-digit"
    }).format(date);
}

function formatSeasonProjection(value: number): string {
    return new Intl.NumberFormat("es-MX", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    }).format(Number(value) || 0);
}

function getDraftFormat(
    value?: string
): "snake" | "linear" {
    return value?.trim().toLowerCase() === "snake"
        ? "snake"
        : "linear";
}

function getDraftFormatLabel(
    value?: string
): string {
    return getDraftFormat(value) === "snake"
        ? "Snake"
        : "Linear";
}

const FantasyDraft: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const [presentToast] = useIonToast();


    const [draftState, setDraftState] =
        useState<DraftState | null>(null);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectingPlayerId, setSelectingPlayerId] = useState("");
    const [error, setError] = useState("");
    const [positionFilter, setPositionFilter] =
        useState<PositionFilter>("ALL");

    const [searchTerm, setSearchTerm] = useState("");

    const [randomizingDraftOrder, setRandomizingDraftOrder] =
    useState(false);

    /*
    Hora oficial estimada del servidor.

    performance.now() es monotónico: modificar la hora o fecha
    del dispositivo no altera su avance.
*/
const serverClockRef = useRef({
    serverTime: 0,
    receivedAt: 0
});

const [clockTick, setClockTick] = useState(
    function () {
        return performance.now();
    }
);


    /*
    Guarda la versión sin provocar renders.
    Se usa para detectar picks nuevos durante el polling.
*/
const draftVersionRef = useRef<number | null>(null);

/*
    Evita iniciar una segunda petición si la anterior
    todavía no ha terminado.
*/
const isPollingRef = useRef(false);

/*
    Guarda los números de selección ya conocidos.

    Así se muestran notificaciones sólo para picks nuevos,
    no para todo el historial al abrir el draft.
*/
const knownPickNumbersRef = useRef<Set<number>>(
    new Set()
);

/*
    Distingue la carga inicial de las actualizaciones posteriores.
*/
const hasLoadedDraftRef = useRef(false);

function synchronizeServerClock(serverNow: string) {
    const serverTime = new Date(serverNow).getTime();

    if (Number.isNaN(serverTime)) {
        return;
    }

    serverClockRef.current = {
        serverTime,
        receivedAt: performance.now()
    };

    /*
        Fuerza un render inmediato tras recibir la hora oficial.
    */
    setClockTick(performance.now());
}

function getEstimatedServerTime(): number {
    const clock = serverClockRef.current;

    /*
        Sólo se usa como respaldo antes de que la primera respuesta
        del servidor haya sido recibida.
    */
    if (clock.serverTime === 0) {
        return Date.now();
    }

    return (
        clock.serverTime +
        (performance.now() - clock.receivedAt)
    );
}

    const loadDraftPollState = useCallback(
    async function (): Promise<FantasyDraftPollState> {
        const accessToken =
            Cookies.get("access_token") || "";

        const accessUsername =
            Cookies.get("access_username") || "";

        const response = await fetch(
            "https://fantasy.ftblscore.com/create_league/" +
                "control_DraftState.php" +
                `?id=${encodeURIComponent(id)}` +
                "&compact=1" +
                `&t=${Date.now()}`,
            {
                method: "GET",
                cache: "no-store",
                credentials: "include",
                headers: {
                    Accept: "application/json",
                    "X-Access-Token": accessToken,
                    "X-Access-Username": accessUsername
                }
            }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
            throw new Error(
                result.message ||
                    "No fue posible actualizar el draft."
            );
        }

        return result;
    },
    [id]
);



    const loadDraftState = useCallback(
        async function (forceRefresh = false) {
            try {
                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }
                const accessToken = Cookies.get("access_token") || "";
                const accessUsername = Cookies.get("access_username") || "";


                const response = await fetch(
                    `https://fantasy.ftblscore.com/create_league/control_DraftState.php?id=${encodeURIComponent(id)}&t=${Date.now()}`,
                    {
                        method: "GET",
                        cache: "no-store",
                        credentials: "include",
                        headers: {
                            Accept: "application/json",
                            "X-Access-Token": accessToken,
                            "X-Access-Username": accessUsername
                        }
                    }
                );


                const result = await response.json();

                if (!response.ok || !result.success) {
    throw new Error(
        result.message ||
            "No fue posible cargar el draft."
    );
}

synchronizeServerClock(result.serverNow);



setError("");

draftVersionRef.current = result.version;

const receivedPicks: DraftPick[] = Array.isArray(
    result.draft
)
    ? result.draft
    : [];

/*
    En la primera carga sólo se registran los picks existentes.
    No se deben mostrar notificaciones por selecciones antiguas.
*/
if (!hasLoadedDraftRef.current) {
    receivedPicks.forEach(function (pick) {
        knownPickNumbersRef.current.add(
            pick.pickNumber
        );
    });

    hasLoadedDraftRef.current = true;
} else {
    const newPicks = receivedPicks
        .filter(function (pick) {
            return !knownPickNumbersRef.current.has(
                pick.pickNumber
            );
        })
        .sort(function (left, right) {
            return left.pickNumber - right.pickNumber;
        });

    newPicks.forEach(function (pick) {
        /*
            Se registra antes de mostrar el toast para evitar
            duplicados si dos actualizaciones terminan muy cerca.
        */
        knownPickNumbersRef.current.add(
            pick.pickNumber
        );

        const isMyPick =
            pick.teamId === result.myTeamId;

        void presentToast({
            message: isMyPick
                ? `Seleccionaste a ${pick.playerName} (${pick.position}).`
                : `${pick.draftTeamName} seleccionó a ${pick.playerName} (${pick.position}).`,
            duration: 3200,
            position: "top",
            color: isMyPick ? "success" : "dark",
            buttons: [
                {
                    text: "OK",
                    role: "cancel"
                }
            ]
        });
    });
}

setDraftState(result);


            } catch (requestError) {
                setError(
                    requestError instanceof Error
                        ? requestError.message
                        : "No fue posible cargar el draft."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id, presentToast]

    );
/*
    Si se abre otra liga sin desmontar el componente,
    no deben conservarse los picks de la liga anterior.
*/
useEffect(
    function () {
        draftVersionRef.current = null;

        knownPickNumbersRef.current.clear();

        hasLoadedDraftRef.current = false;
    },
    [id]
);

    useEffect(
        function () {
            loadDraftState();
        },
        [loadDraftState]
    );

    /*
        Sólo actualiza el texto del reloj local.
        El tiempo real siempre depende de deadlineAt generado por PHP.
    */
useEffect(function () {
    const intervalId = window.setInterval(function () {
        /*
            Sólo actualiza el render.
            No consulta ni usa la hora civil del dispositivo.
        */
        setClockTick(performance.now());
    }, 1000);

    return function () {
        window.clearInterval(intervalId);
    };
}, []);


    /*
    Polling ligero.

    Consulta sólo estado, turno, versión y contador.
    La lista completa de jugadores sólo se descarga cuando
    control_DraftPick.php modifica la versión del draft.
*/
useEffect(
    function () {
        let isMounted = true;

        async function pollDraft() {
            if (
                document.visibilityState !== "visible" ||
                isPollingRef.current
            ) {
                return;
            }

            try {
                isPollingRef.current = true;

                const pollState =
    await loadDraftPollState();

if (!isMounted) {
    return;
}

synchronizeServerClock(pollState.serverNow);


                const previousVersion =
                    draftVersionRef.current;

                const hasNewPick =
                    previousVersion !== null &&
                    previousVersion !== pollState.version;

                /*
                    Actualiza sólo los datos ligeros.
                */
                setDraftState(function (previousState) {
                    if (!previousState) {
                        return previousState;
                    }

                    return {
                        ...previousState,
                        serverNow: pollState.serverNow,
                        leagueName: pollState.leagueName,
                        status: pollState.status,
                        version: pollState.version,
                        pickNumber: pollState.pickNumber,
                        totalPicks: pollState.totalPicks,
                        secondsPerPick:
                            pollState.secondsPerPick,

                        draftStartAt: pollState.draftStartAt,

                        turnStartedAt:
                            pollState.turnStartedAt,

                        deadlineAt: pollState.deadlineAt,
                        currentTeam: pollState.currentTeam,
                        myTeamId: pollState.myTeamId,
 
                        draftOrderRandomization: pollState.draftOrderRandomization
                    };

                });

                draftVersionRef.current =
                    pollState.version;

                /*
                    Si alguien hizo una selección, se actualizan:
                    - jugadores disponibles;
                    - historial;
                    - mi roster.
                */
                if (hasNewPick) {
                    await loadDraftState(true);
                }
            } catch (pollError) {
                /*
                    Un fallo temporal no debe borrar la pantalla
                    ni mostrar un error invasivo cada dos segundos.
                */
                console.warn(
                    "No fue posible actualizar el estado del draft:",
                    pollError
                );
            } finally {
                isPollingRef.current = false;
            }
        }

        const intervalId = window.setInterval(
            pollDraft,
            2000
        );

        return function () {
            isMounted = false;
            window.clearInterval(intervalId);
        };
    },
    [
        loadDraftPollState,
        loadDraftState
    ]
);



    const isMyTurn =
        draftState?.status === "active" &&
        draftState.currentTeam?.id === draftState.myTeamId;

    const filteredPlayers = useMemo(
        function () {
            if (!draftState) {
                return [];
            }

            const normalizedSearch = searchTerm
                .trim()
                .toLocaleLowerCase("es-MX");

            return draftState.availablePlayers.filter(function (
                player
            ) {
                const matchesPosition =
                    positionFilter === "ALL" ||
                    player.position === positionFilter;

                const matchesSearch =
                    !normalizedSearch ||
                    player.name
                        .toLocaleLowerCase("es-MX")
                        .includes(normalizedSearch) ||
                    player.teamName
                        .toLocaleLowerCase("es-MX")
                        .includes(normalizedSearch);

                return matchesPosition && matchesSearch;
            });
        },
        [draftState, positionFilter, searchTerm]
    );

    const recentPicks = useMemo(
        function () {
            if (!draftState) {
                return [];
            }

            return [...draftState.draft]
                .slice(-8)
                .reverse();
        },
        [draftState]
    );

    async function selectPlayer(playerId: string) {
        if (!draftState || !isMyTurn || selectingPlayerId) {
            return;
        }

        try {
            setSelectingPlayerId(playerId);
            setError("");

            const accessToken = Cookies.get("access_token") || "";
            const accessUsername = Cookies.get("access_username") || "";

            const response = await fetch(
                "https://fantasy.ftblscore.com/create_league/control_DraftPick.php",
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        "X-Access-Token": accessToken,
                        "X-Access-Username": accessUsername
                    },
                    body: JSON.stringify({
                        leagueId: id,
                        playerId
                    })
                }
            );


            const result = await response.json();

            if (!response.ok) {
                throw new Error(
                    result.message ||
                        "No fue posible seleccionar al jugador."
                );
            }

            await loadDraftState(true);
        } catch (requestError) {
            setError(
                requestError instanceof Error
                    ? requestError.message
                    : "No fue posible registrar la selección."
            );

            /*
                Si otro manager eligió al jugador antes, esta consulta
                elimina al jugador de la lista local.
            */
            await loadDraftState(true);
        } finally {
            setSelectingPlayerId("");
        }
    }

    async function randomizeDraftOrder() {
    if (
        !draftState?.draftOrderRandomization?.canRandomize ||
        randomizingDraftOrder
    ) {
        return;
    }

    try {
        setRandomizingDraftOrder(true);
        setError("");

        const accessToken =
            Cookies.get("access_token") || "";

        const accessUsername =
            Cookies.get("access_username") || "";

        const response = await fetch(
            "https://fantasy.ftblscore.com/create_league/" +
                "control_RandomizeDraftOrder.php",
            {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-Access-Token": accessToken,
                    "X-Access-Username": accessUsername
                },
                body: JSON.stringify({
                    leagueId: id
                })
            }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
            throw new Error(
                result.message ||
                    "No fue posible aleatorizar el orden."
            );
        }

        await presentToast({
            message:
                result.message ||
                "El orden del draft fue aleatorizado.",
            duration: 3000,
            position: "top",
            color: "success"
        });

        /*
            Recarga el orden de primera ronda y el estado
            para ocultar el botón después del único uso permitido.
        */
        await loadDraftState(true);
    } catch (requestError) {
        const message =
            requestError instanceof Error
                ? requestError.message
                : "No fue posible aleatorizar el orden.";

        setError(message);

        await presentToast({
            message,
            duration: 3200,
            position: "top",
            color: "danger"
        });

        await loadDraftState(true);
    } finally {
        setRandomizingDraftOrder(false);
    }
}

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadDraftState(true);
        event.detail.complete();
    }

    function goToLeague() {
        history.replace(`/fantasyLeague/${id}`);
    }

    if (loading && !draftState) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>Draft</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent>
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (!draftState) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar a la liga"
                                onClick={goToLeague}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Draft</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="fantasy-draft-content">
                    <div className="fantasy-draft-empty">
                        <IonIcon icon={refreshOutline} />
                        <strong>No fue posible abrir el draft</strong>
                        <span>{error}</span>

                        <IonButton
                            fill="outline"
                            onClick={function () {
                                loadDraftState(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

/*
    clockTick provoca el render cada segundo.
    La hora calculada proviene del último serverNow recibido
    y avanza con performance.now().
*/
void clockTick;

const estimatedServerTime = getEstimatedServerTime();

const timer =
    draftState.status === "active"
        ? formatRemainingTime(
              draftState.deadlineAt,
              estimatedServerTime
          )
        : "";

const scheduledTimer =
    draftState.status === "scheduled"
        ? formatRemainingTime(
              draftState.draftStartAt,
              estimatedServerTime
          )
        : "";



    const statusLabel =
        draftState.status === "scheduled"
            ? "PROGRAMADO"
            : draftState.status === "completed"
            ? "FINALIZADO"
            : isMyTurn
            ? "TU TURNO"
            : "EN CURSO";

const draftFormat = getDraftFormat(
    draftState.draftFormat
);

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar className="fantasy-draft-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar a la liga"
                            onClick={goToLeague}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Draft</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar draft"
                            disabled={refreshing}
                            onClick={function () {
                                loadDraftState(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent
                className="fantasy-draft-content"
            >
                <IonRefresher
                    slot="fixed"
                    onIonRefresh={handleRefresh}
                >
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando draft..."
                    />
                </IonRefresher>

                <section className="fantasy-draft-hero">
                    <div className="fantasy-draft-hero__icon">
                        <IonIcon icon={trophyOutline} />
                    </div>

                    <div>
                        <span>DRAFT FANTASY</span>
                        <h1>{draftState.leagueName}</h1>

                        <p>
                            Selección {Math.min(
                                draftState.pickNumber,
                                draftState.totalPicks
                            )}{" "}
                            de {draftState.totalPicks}
                        </p>
                    </div>

                    <IonBadge
                        className={`fantasy-draft-status fantasy-draft-status--${draftState.status}`}
                    >
                        {statusLabel}
                    </IonBadge>
                </section>

                {error && (
                    <div className="fantasy-draft-error">
                        {error}
                    </div>
                )}

                <main className="fantasy-draft-main">
<section
    className={[
        "fantasy-draft-turn",
        draftState.status !== "completed"
            ? "fantasy-draft-turn--sticky"
            : "",
        isMyTurn
            ? "fantasy-draft-turn--mine"
            : ""
    ].join(" ")}
    aria-live="polite"
>

                        <div className="fantasy-draft-turn__top">
                            <span>
                                {draftState.status === "scheduled"
                                    ? "PRÓXIMAMENTE"
                                    : draftState.status === "completed"
                                    ? "DRAFT TERMINADO"
                                    : "EN EL RELOJ"}
                            </span>

                            {draftState.status === "active" && (
    <div className="fantasy-draft-turn__timer">
        <IonIcon icon={timerOutline} />
        <strong>{timer}</strong>
    </div>
)}

{draftState.status === "scheduled" && (
    <div className="fantasy-draft-turn__timer">
        <IonIcon icon={hourglassOutline} />
        <strong>{scheduledTimer}</strong>
    </div>
)}

                        </div>

                        {draftState.status === "active" &&
                        draftState.currentTeam ? (
                            <div className="fantasy-draft-turn__team">
                                <span className="fantasy-draft-turn__avatar">
                                    {getInitial(
                                        draftState.currentTeam.name
                                    )}
                                </span>

                                <div>
                                    <strong>
                                        {isMyTurn
                                            ? "Es tu turno"
                                            : draftState.currentTeam.name}
                                    </strong>

                                    <small>
                                        {isMyTurn
                                            ? "Selecciona un jugador antes de que termine el reloj."
                                            : draftState.currentTeam
                                                  .manager
                                            ? `Manager: ${draftState.currentTeam.manager}`
                                            : "Esperando su selección."}
                                    </small>
                                </div>
                            </div>
                        ) : (
                            <div className="fantasy-draft-turn__team">
                                <IonIcon icon={hourglassOutline} />

                                <div>
                                    <strong>
                                        {draftState.status === "scheduled"
    ? `El draft inicia en ${scheduledTimer}`

                                            : "Todas las selecciones fueron completadas"}
                                    </strong>

                                    <small>
                                        {draftState.status === "scheduled"
                                            ? "La sala se actualizará automáticamente cuando comience."
                                            : "Consulta tu roster para revisar tu equipo final."}
                                    </small>
                                </div>
                            </div>
                        )}
                    </section> 
{draftState.status === "scheduled" &&
draftState.draftOrderRandomization?.isCommissioner &&
!draftState.draftOrderRandomization.wasRandomized ? (
    <section className="fantasy-draft-randomize">
        <div>
            <span>COMISIONADO</span>

            <strong>Aleatorizar posiciones del draft</strong>

            <small>
                {draftState.draftOrderRandomization.canRandomize
                    ? "El sorteo definirá el orden de la primera ronda. Esta acción sólo se puede realizar una vez."
                    : `Participantes: ${draftState.draftOrderRandomization.registeredTeams} de ${draftState.draftOrderRandomization.requiredTeams}.`}
            </small>
        </div>

        <IonButton
            color="primary"
            disabled={
                !draftState.draftOrderRandomization.canRandomize ||
                randomizingDraftOrder
            }
            onClick={randomizeDraftOrder}
        >
            {randomizingDraftOrder
                ? "Sorteando..."
                : "Aleatorizar orden"}
        </IonButton>

        {!draftState.draftOrderRandomization.canRandomize &&
        draftState.draftOrderRandomization.reason ? (
            <small className="fantasy-draft-randomize__reason">
                {draftState.draftOrderRandomization.reason}
            </small>
        ) : null}
    </section>
) : null}

{draftState.status === "scheduled" &&
draftState.firstRoundOrder &&
draftState.firstRoundOrder.length > 0 ? (
    <section className="fantasy-draft-order">
        <div className="fantasy-draft-order__heading">
            <div>
                <span>ORDEN DEL DRAFT</span>
                <h2>Primera ronda</h2>
            </div>

<IonBadge color="primary">
    {getDraftFormatLabel(
        draftState.draftFormat
    )}
</IonBadge>

        </div>
<p className="fantasy-draft-order__description">
    {draftFormat === "snake"
        ? "Este será el orden de las selecciones en la primera ronda. En las rondas pares, el orden se invierte."
        : "Este será el orden de las selecciones. El mismo orden de la primera ronda se mantiene durante todo el draft."}
</p>
        <ol className="fantasy-draft-order__list">
            {draftState.firstRoundOrder.map(
                function (team, index) {
                    const isMyTeam =
                        team.id === draftState.myTeamId;

                    return (
                        <li
                            className={[
                                "fantasy-draft-order__team",
                                isMyTeam
                                    ? "fantasy-draft-order__team--mine"
                                    : ""
                            ].join(" ")}
                            key={team.id}
                        >
                            <span className="fantasy-draft-order__number">
                                {index + 1}
                            </span>

                            <span className="fantasy-draft-order__avatar">
                                {getInitial(team.name)}
                            </span>

                            <div>
                                <strong>
                                    {team.name}
                                    {isMyTeam
                                        ? " (Tú)"
                                        : ""}
                                </strong>

                                {team.manager ? (
                                    <small>
                                        Manager: {team.manager}
                                    </small>
                                ) : null}
                            </div>

                            {isMyTeam ? (
                                <IonBadge color="success">
                                    TU EQUIPO
                                </IonBadge>
                            ) : null}
                        </li>
                    );
                }
            )}
        </ol>
    </section>
) : null}

                    <section className="fantasy-draft-section">
                        <div className="fantasy-draft-section__heading">
                            <div>
                                <span>DISPONIBLES</span>
                                <h2>Selecciona un jugador</h2>
                            </div>

                            <IonBadge>
                                {filteredPlayers.length}
                            </IonBadge>
                        </div>

                        <IonSearchbar
                            value={searchTerm}
                            debounce={200}
                            placeholder="Buscar jugador o equipo"
                            onIonInput={function (event) {
                                const searchbar =
                                    event.currentTarget as HTMLIonSearchbarElement;

                                setSearchTerm(searchbar.value || "");
                            }}

                        />

                        <IonSegment
                            className="fantasy-draft-filters"
                            value={positionFilter}
                            scrollable
                            onIonChange={function (event) {
                                setPositionFilter(
                                    event.detail
                                        .value as PositionFilter
                                );
                            }}
                        >
                            <IonSegmentButton value="ALL">
                                Todos
                            </IonSegmentButton>

                            <IonSegmentButton value="QB">
                                QB
                            </IonSegmentButton>

                            <IonSegmentButton value="RB">
                                RB
                            </IonSegmentButton>

                            <IonSegmentButton value="WR">
                                WR
                            </IonSegmentButton>

                            <IonSegmentButton value="K">
                                K
                            </IonSegmentButton>

                            <IonSegmentButton value="DEF">
                                DEF
                            </IonSegmentButton>
                        </IonSegment>

                        <div className="fantasy-draft-player-list">
                            {filteredPlayers.map(function (player) {
                                const isSelecting =
                                    selectingPlayerId === player.id;

                                return (
                                    <article
                                        className="fantasy-draft-player"
                                        key={player.id}
                                    >
                                        <span
                                            className={`fantasy-draft-player__position fantasy-draft-player__position--${player.position.toLowerCase()}`}
                                        >
                                            {player.position}
                                        </span>

                                        <div className="fantasy-draft-player__details">
                                            <strong>{player.name}</strong>
<small className="fantasy-draft-player__team">
    {player.teamName || "Equipo no disponible"}
</small>

<small className="fantasy-draft-player__projection">
    Proyección: {formatSeasonProjection(
        player.seasonProjection
    )} pts
</small>

                                        </div>

                                        <IonButton
                                            size="small"
                                            disabled={
                                                !isMyTurn ||
                                                Boolean(
                                                    selectingPlayerId
                                                )
                                            }
                                            onClick={function () {
                                                selectPlayer(player.id);
                                            }}
                                        >
                                            {isSelecting
                                                ? "Guardando"
                                                : "Elegir"}
                                        </IonButton>
                                    </article>
                                );
                            })}

                            {filteredPlayers.length === 0 && (
                                <div className="fantasy-draft-no-results">
                                    <IonIcon icon={peopleOutline} />
                                    <strong>
                                        No hay jugadores disponibles
                                    </strong>
                                    <span>
                                        Ajusta los filtros o espera la
                                        siguiente actualización.
                                    </span>
                                </div>
                            )}
                        </div>
                    </section>

                    <section className="fantasy-draft-section">
                        <div className="fantasy-draft-section__heading">
                            <div>
                                <span>HISTORIAL</span>
                                <h2>Últimas selecciones</h2>
                            </div>

                            <IonIcon icon={americanFootballOutline} />
                        </div>

                        <div className="fantasy-draft-picks">
                            {recentPicks.map(function (pick) {
                                return (
                                    <article
                                        className="fantasy-draft-pick"
                                        key={`${pick.pickNumber}-${pick.playerId}`}
                                    >
                                        <span className="fantasy-draft-pick__number">
                                            #{pick.pickNumber}
                                        </span>

                                        <div>
                                            <strong>
                                                {pick.playerName}
                                            </strong>

                                            <small>
                                                {pick.position} · {pick.playerTeamName}

                                            </small>
                                        </div>

                                        <span className="fantasy-draft-pick__team">
                                            {pick.draftTeamName}
                                        </span>
                                    </article>
                                );
                            })}

                            {recentPicks.length === 0 && (
                                <div className="fantasy-draft-no-results">
                                    <IonIcon icon={personOutline} />
                                    <strong>
                                        Todavía no hay selecciones
                                    </strong>
                                    <span>
                                        El historial aparecerá cuando
                                        inicie el draft.
                                    </span>
                                </div>
                            )}
                        </div>
                    </section>

                    <section className="fantasy-draft-section">
                        <div className="fantasy-draft-section__heading">
                            <div>
                                <span>MI ROSTER</span>
                                <h2>
                                    {draftState.myRoster.length} jugadores
                                </h2>
                            </div>

                            <IonIcon icon={checkmarkCircleOutline} />
                            <div className="fantasy-draft-my-roster">
    {draftState.myRoster.map(function (player) {
        return (
            <article
                className="fantasy-draft-my-roster__player"
                key={player.id}
            >
                <span>
                    {player.position}
                </span>

                <div>
                    <strong>{player.name}</strong>

                    <small>
                        Slot: {player.slot || "Pendiente"}
                    </small>
                </div>
            </article>
        );
    })}

    {draftState.myRoster.length === 0 && (
        <div className="fantasy-draft-no-results">
            <IonIcon icon={personOutline} />

            <strong>Aún no tienes jugadores</strong>

            <span>
                Tus selecciones aparecerán aquí durante el draft.
            </span>
        </div>
    )}
</div>

                        </div>
                    </section>
                </main>
            </IonContent>
        </IonPage>
    );
};

export default FantasyDraft;
